import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { theme, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import CustomTable from 'components/Tables/CustomTable';
import { RootState } from 'store/configureStore';
import ApiService from 'utils/ApiCalls';
import { controlInventoryReportColumns } from 'constants/reports';
import { IControlReportData } from 'store/ducks/Reporting/types';
import dayjs from 'dayjs';
import { useReportExport } from 'utils/hooks/useReportExport';
import * as Styled from 'components/Layouts/Layout.styled';
import * as LocalStyled from '../ControlReport.styled';
import * as ReportingStyled from '../../Reporting.styled';

const { Text } = Typography;
const { useToken } = theme;

const ControlInventoryReport: React.FC = () => {
  const { scheme, lang } = useSelector((state: RootState) => ({
    scheme: state.global.scheme,
    lang: state.global.lang
  }));
  const { token } = useToken();
  const { t } = useTranslation();
  const { registerReport, clearReport } = useReportExport();
  const [dataSource, setDataSource] = useState<IControlReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [filters, setFilters] = useState<{
    group_ids?: string;
    division_ids?: string;
    region_ids?: string;
    legal_entity_ids?: string;
    business_unit_ids?: string;
  }>({});
  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [divisionOptions, setDivisionOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [regionOptions, setRegionOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [businessUnitOptions, setBusinessUnitOptions] = useState<
    { label: string; value: number }[]
  >([]);

  useEffect(() => {
    const columns = controlInventoryReportColumns(dataSource);

    registerReport(
      dataSource,
      columns,
      'Control_Inventory_Report',
      'Control Inventory'
    );

    return () => {
      clearReport();
    };
  }, [dataSource, registerReport, clearReport]);

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const [groups, divisions, regions, legalEntities, businessUnits] =
          await Promise.all([
            ApiService.getGroupBPR(),
            ApiService.getDivisionBPR(),
            ApiService.getRegionBPR(),
            ApiService.getLegalEntitiesBPR(),
            ApiService.getBusinessUnitBPR()
          ]);
        setGroupOptions(
          groups.data.data.map((g: { id: number; name: string }) => ({
            label: g.name,
            value: g.id
          }))
        );
        setDivisionOptions(
          divisions.data.data.map((d: { id: number; name: string }) => ({
            label: d.name,
            value: d.id
          }))
        );
        setRegionOptions(
          regions.data.data.map((r: { id: number; name: string }) => ({
            label: r.name,
            value: r.id
          }))
        );
        setLegalEntityOptions(
          legalEntities.data.data.map((l: { id: number; name: string }) => ({
            label: l.name,
            value: l.id
          }))
        );
        setBusinessUnitOptions(
          businessUnits.data.data.map((b: { id: number; name: string }) => ({
            label: b.name,
            value: b.id
          }))
        );
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
      }
    };
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (!shouldFetch) return;

    const fetchControlReports = async () => {
      setLoading(true);
      try {
        const params: string[] = [];

        if (filters.group_ids) params.push(`group_ids=${filters.group_ids}`);
        if (filters.division_ids)
          params.push(`division_ids=${filters.division_ids}`);
        if (filters.region_ids) params.push(`region_ids=${filters.region_ids}`);
        if (filters.legal_entity_ids)
          params.push(`legal_entity_ids=${filters.legal_entity_ids}`);
        if (filters.business_unit_ids)
          params.push(`business_unit_ids=${filters.business_unit_ids}`);
        if (selectedDate)
          params.push(`date=${selectedDate.format('YYYY-MM-DD')}`);

        const response = await ApiService.getControlReports(
          params.length > 0 ? params.join('&') : undefined
        );
        const apiData = response.data?.data;
        setDataSource(Array.isArray(apiData) ? apiData : []);
      } catch {
        setDataSource([]);
      } finally {
        setLoading(false);
        setShouldFetch(false);
      }
    };

    fetchControlReports();
  }, [selectedDate, shouldFetch, filters]);

  const handleDisplay = () => {
    setShouldFetch(true);
  };

  return (
    <>
      <LocalStyled.GlobalDatePickerStyle token={token} />
      <ReportingStyled.ReportTableWrapper>
        <LocalStyled.ControlReportFilterBar>
          <Styled.HorizontalFilterBar token={token} scheme={scheme}>
            <Styled.FilteredContainerRisks>
              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('GROUP')}
                options={groupOptions}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    group_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />
              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('DIVISION')}
                options={divisionOptions}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    division_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />
              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('REGION')}
                options={regionOptions}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    region_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />
              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('LEGALENTITY')}
                options={legalEntityOptions}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    legal_entity_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />
              <LocalStyled.EntitySelect
                mode='multiple'
                placeholder={t('BUSINESSUNIT')}
                options={businessUnitOptions}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    business_unit_ids:
                      Array.isArray(value) && value.length > 0
                        ? value.join(',')
                        : undefined
                  })
                }
                allowClear
                popupMatchSelectWidth={false}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />
              <LocalStyled.StyledDatePicker
                placeholder={t('SELECT_DATE')}
                value={selectedDate}
                onChange={(date) => setSelectedDate(date as dayjs.Dayjs | null)}
                format='YYYY-MM-DD'
                allowClear
                popupClassName='white-bg-datepicker'
              />
              <Styled.Spacer />
              <Styled.StyledButton
                type='primary'
                onClick={handleDisplay}
                className='display-save-button'
              >
                {t('DISPLAY')}
              </Styled.StyledButton>
            </Styled.FilteredContainerRisks>
          </Styled.HorizontalFilterBar>
        </LocalStyled.ControlReportFilterBar>

        <LocalStyled.ControlReportTableWrapper>
          <CustomTable
            drawTable
            itemsCount={dataSource.length}
            columns={controlInventoryReportColumns(dataSource)}
            dataSource={dataSource}
            loading={loading}
            rowKey={(record: IControlReportData) => record.control_id}
            scroll={{ x: 2000, y: 420 }}
            sticky
            scheme={scheme}
          />
        </LocalStyled.ControlReportTableWrapper>
      </ReportingStyled.ReportTableWrapper>
    </>
  );
};

export default ControlInventoryReport;
